/**
 * The sky — one SVG canvas at four zooms.
 *
 * There is exactly ONE drawing.  "Sky", "ring", "column" and "world"
 * are camera states over it (a scale + translate on a single `<g>`, see
 * `layout.ts`), not separate screens, which is what makes zooming feel
 * like flying rather than navigating.  Back-out is always the same
 * gesture: tap the space around.
 *
 * Everything drawn here comes from the payload: the rings and their
 * order from the registry taxonomy, each world's place from its ring and
 * its family (`layoutSky`), its name from where the label engine seats
 * it (`layoutLabels`), its LOOK from `planetLook` (ring + name), and every
 * fill, glow and gauge from the learner's own folded log.
 *
 * The disc is cut into family wedges — the altitudes seating: the ring
 * is how deep, the wedge is which family — and every other wedge takes
 * a wash (the weave), with the family names riding the rim.  Those are
 * decided readings (`settings.DRAWING`), not switches.
 */

import { memo, useEffect, useMemo, useRef, useState } from "react";

import { Mastery, type CreditStyle } from "./Mastery";
import { OpenPlanet } from "./OpenPlanet";
import { PlanetBody } from "./PlanetBody";
import { arcPath } from "./geometry";
import {
  GLYPH, type Box, type LabelMode, type LabelSubject, type Placement, layoutLabels, rowsAltOf, rowsOf,
} from "./labels";
import {
  CENTRE,
  DRIFT,
  FIELD,
  GATE,
  INNER,
  RIM,
  SKY_CAMERA,
  type Camera,
  type Point,
  type SkyLayout,
  layoutSky,
  polar,
  ringCamera,
  sectorCamera,
  sectorLabelAngle,
  wedgePath,
  worldCamera,
} from "./layout";
import {
  MONO, type Palette, gaugeColours, ringColour, seasonColour, sectorColour, trackColour, weaveColour,
} from "./palette";
import { driftOf, hash32, shortLabel } from "./planetLook";
import type { Guide, Links, Unlit } from "./settings";
import type { AtlasPayload, PlanetReading, SectorReading } from "./types";
import { useViewMemo } from "./useViewMemo";

export const KEYFRAMES =
  "@keyframes zfade{from{opacity:0}to{opacity:1}}" +
  "@keyframes zbreathe{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}" +
  "@keyframes zember{0%,100%{opacity:0.26}50%{opacity:0.5}}" +
  "@keyframes zburn{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(1.4)}}" +
  "@keyframes zdriftA{from{transform:rotate(0deg)}to{transform:rotate(1.4deg)}}" +
  "@keyframes zdriftB{from{transform:rotate(0deg)}to{transform:rotate(-1.4deg)}}" +
  "@keyframes zshoot{0%{transform:translate(0px,0px);opacity:0}2%{opacity:0.9}11%{transform:translate(200px,128px);opacity:0}100%{transform:translate(200px,128px);opacity:0}}" +
  "@keyframes zsun{0%,100%{opacity:0.45}50%{opacity:1}}";

const NSS = { vectorEffect: "non-scaling-stroke" as const };

/**
 * How solid a thing must be drawn to be worth tapping.
 *
 * A zoomed view fades everything it is not about, and a faded thing
 * reads as disabled — so a tap on one is a tap on the BACKGROUND: it
 * means "out of here", not "into that".  Rather than teach every object
 * to answer a tap two ways, a faded one simply refuses the pointer and
 * lets the tap fall through to the backdrop, whose job this already is.
 *
 * Only inside a zoom: the open sky has no parent to fall back to, so a
 * world the reader has filtered down is still theirs to open.
 */
const LIVE = 0.5;
const taps = (zoomedIn: boolean, opacity: number): "auto" | "none" =>
  !zoomedIn || opacity >= LIVE ? "auto" : "none";

/**
 * The camera's move.  It leaves AT ONCE — the old curve held still for
 * its first third, which on a tap reads as the tap having missed — and
 * spends its length settling instead.
 */
const GLIDE = "560ms cubic-bezier(0.16,1,0.3,1)";
const ROMAN = ["Ⅰ", "Ⅱ", "Ⅲ", "Ⅳ", "Ⅴ", "Ⅵ", "Ⅶ", "Ⅷ", "Ⅸ", "Ⅹ"];
const roman = (slot: number) => ROMAN[slot] ?? String(slot + 1);
/** The rim's type does not grow with the phone's: it is a caption, and a
    long family name at phone size would run into its neighbour's wedge. */
const RIM_FONT = 4.6;
/**
 * At sky zoom, forty names share one field, and the two forces on their
 * size pull against each other: a phone renders the fixed viewBox at
 * fewer pixels, so the type has to grow to stay readable, while the
 * field only has room for so much of it.  This is where they meet — the
 * smallest scale that still clears the readable floor on a phone (the
 * browser suite measures it), and no larger.  The ring and column
 * zooms, which have the room, take the phone's full scale.
 */
export const SKY_LABEL_SCALE_MAX = 1.42;
export const labelScale = (mode: Mode, textScale: number) =>
  mode === "sky" ? Math.min(textScale, SKY_LABEL_SCALE_MAX) : textScale;

type Mode = "sky" | "ring" | "sector" | "world";

type Props = {
  data: AtlasPayload;
  P: Palette;
  focus: string | null;
  ringSel: string | null;
  sectorSel: string | null;
  selClaim: number | null;
  credit: CreditStyle;
  /** Whether a ring's thread lights only between consecutive opened
      worlds of one family ("chain") or under every opened world ("trail"). */
  links: Links;
  guide: Guide;
  /** The chosen audience, whose route wears a halo. */
  track: string | null;
  unlit: Unlit;
  /** Multiplies every in-map label.  The viewBox is fixed, so a phone
      renders the same units at fewer CSS pixels — the sky's type has to
      grow to stay readable rather than the map having to shrink. */
  textScale: number;
  onPlanet: (id: string) => void;
  onRing: (id: string | null) => void;
  onSector: (id: string | null) => void;
  onClaim: (planetId: string, index: number) => void;
  onClose: () => void;
};

/** What the sky knows about one world, computed once per payload. */
type World = {
  planet: PlanetReading;
  at: Point;
  slot: number | null;
  /** The family wedge it sits in — "" for a world filed under none. */
  sector: string;
  name: string;
};

const nameOf = (planet: PlanetReading) => planet.skyName || shortLabel(planet.title, planet.id);

/**
 * What the names are laid out FOR.
 *
 * An opened world is not a view of its own: it draws no names, and it
 * sits over the ring or column the reader came from — so it keeps that
 * one, which is also the one it will glide back to.
 */
type View = { mode: LabelMode; ring: string | null; sector: string | null };
const viewOf = (ring: string | null, sector: string | null): View =>
  sector ? { mode: "sector", ring: null, sector }
    : ring !== null ? { mode: "ring", ring, sector: null }
      : { mode: "sky", ring: null, sector: null };
const viewKey = (view: View) => `${view.mode}\u0000${view.ring ?? ""}\u0000${view.sector ?? ""}`;

/** How a view is framed — the same answer for a view whether or not it is on screen. */
function cameraFor(view: View, sky: SkyLayout, worlds: Map<string, World>, ringCount: number): Camera {
  if (view.sector) {
    const members: Point[] = [];
    worlds.forEach((world) => {
      if (world.sector === view.sector && !world.planet.sun && world.slot !== null) members.push(world.at);
    });
    return sectorCamera(members);
  }
  if (view.ring !== null) {
    const slot = sky.slots.get(view.ring);
    if (slot !== undefined) return ringCamera(slot, ringCount);
  }
  return SKY_CAMERA;
}

export function Orrery(props: Props) {
  const {
    data, P, focus, ringSel, sectorSel, selClaim, credit, links, guide, track, unlit, textScale,
  } = props;
  const gauge = useMemo(() => gaugeColours(P), [P]);
  const mode: Mode = focus ? "world" : sectorSel ? "sector" : ringSel !== null ? "ring" : "sky";
  const zoomed = mode === "world";
  /** The camera is inside something, so there is a level to fall back to. */
  const zoomedIn = mode !== "sky";

  // ── the geometry ───────────────────────────────────────────────────
  const sectorOrder = useMemo(() => data.sectors.map((sector) => sector.id), [data.sectors]);
  const sky = useMemo(
    () =>
      layoutSky(
        data.rings,
        data.planets.map((planet) => ({
          id: planet.id,
          ring: planet.ringLevel === null ? null : planet.ring,
          sector: planet.sector,
          sun: planet.sun,
        })),
        data.unplaced,
        sectorOrder,
      ),
    [data.rings, data.planets, data.unplaced, sectorOrder],
  );
  const known = useMemo(() => new Set(sectorOrder), [sectorOrder]);
  const worlds = useMemo(() => {
    const out = new Map<string, World>();
    data.planets.forEach((planet) => {
      const at = sky.positions.get(planet.id);
      if (!at) return;
      out.set(planet.id, {
        planet,
        at,
        slot: planet.ringLevel === null ? null : (sky.slots.get(planet.ring) ?? null),
        sector: known.has(planet.sector) ? planet.sector : "",
        name: nameOf(planet),
      });
    });
    return out;
  }, [data.planets, sky, known]);
  const last = data.rings.length - 1;
  const hasSun = sky.sun !== null;

  /** Each ring's worlds by family, in ring order — the chain's groups. */
  const groups = useMemo(() => {
    const out = new Map<string, string[][]>();
    data.rings.forEach((ring) => {
      const bySector = new Map<string, string[]>();
      ring.planets.forEach((id) => {
        const world = worlds.get(id);
        if (!world || world.planet.sun) return;
        const list = bySector.get(world.sector) ?? [];
        list.push(id);
        bySector.set(world.sector, list);
      });
      out.set(ring.id, sky.sectors.map((sector) => bySector.get(sector) ?? []).filter((list) => list.length));
    });
    return out;
  }, [data.rings, worlds, sky.sectors]);

  /** A family's tally: worlds and crossings, the sun not counted. */
  const tallies = useMemo(() => {
    const out = new Map<string, { total: number; crossed: number }>();
    sky.sectors.forEach((sector) => out.set(sector, { total: 0, crossed: 0 }));
    worlds.forEach((world) => {
      if (world.planet.sun || world.slot === null) return;
      const tally = out.get(world.sector);
      if (!tally) return;
      tally.total += 1;
      if (world.planet.state === "CROSSED") tally.crossed += 1;
    });
    return out;
  }, [worlds, sky.sectors]);
  const sectorInfo = useMemo(() => new Map(data.sectors.map((sector) => [sector.id, sector])), [data.sectors]);

  // ── the camera ─────────────────────────────────────────────────────
  const here = viewOf(ringSel, sectorSel);
  const cam: Camera = useMemo(() => {
    if (focus) {
      const at = sky.positions.get(focus);
      if (at) return worldCamera(at);
    }
    return cameraFor(viewOf(ringSel, sectorSel), sky, worlds, data.rings.length);
  }, [focus, sectorSel, ringSel, sky, worlds, data.rings.length]);

  /** The rim labels' angles, once per layout: the sky and the label engine both need them. */
  const rimLabels = useMemo(() => {
    const out = new Map<string, { text: string; angle: number; halfDeg: number }>();
    const rl = RIM + 6.5;
    sky.sectors.forEach((sector) => {
      const span = sky.spans.get(sector);
      if (!span) return;
      const info = sectorInfo.get(sector);
      const tally = tallies.get(sector) ?? { total: 0, crossed: 0 };
      const text = info
        ? `${info.symbol} ${info.label.toUpperCase()} ${tally.crossed}/${tally.total}`
        : `UNFILED ${tally.crossed}/${tally.total}`;
      const width = text.length * RIM_FONT * GLYPH * 1.18;
      const halfDeg = ((width / 2 / rl) * 180) / Math.PI;
      const outerAngles: number[] = [];
      worlds.forEach((world) => {
        if (world.sector === sector && world.slot === last) outerAngles.push(sky.angles.get(world.planet.id) as number);
      });
      out.set(sector, { text, angle: sectorLabelAngle(span, outerAngles, halfDeg), halfDeg });
    });
    return out;
  }, [sky, sectorInfo, tallies, worlds, last]);

  // ── the names ──────────────────────────────────────────────────────
  /** Every view the map has, so the ones off screen can be seated while
      the reader looks at this one — see `useViewMemo`. */
  const views = useMemo(() => {
    const out = new Map<string, View>();
    const add = (view: View) => out.set(viewKey(view), view);
    add(viewOf(null, null));
    data.rings.forEach((ring) => add(viewOf(ring.id, null)));
    sky.sectors.forEach((sector) => add(viewOf(null, sector)));
    return out;
  }, [data.rings, sky.sectors]);
  const warmViews = useMemo(() => [...views.keys()], [views]);

  const placements = useViewMemo(viewKey(here), (key) => {
    // Every key the hook can hand back is one of `views`; the fallback is
    // for a ring or column the taxonomy no longer lists, which is still
    // the view on screen and still deserves its names.
    const view = views.get(key) ?? here;
    const labelMode = view.mode;
    const near = labelMode !== "sky";
    const onView = (world: World) =>
      labelMode === "sky" ? true
        : labelMode === "ring" ? world.planet.ring === view.ring
          : world.sector === view.sector || world.planet.sun;
    const isSubject = (world: World) =>
      labelMode === "sky" ? true : labelMode === "ring" ? onView(world) : world.sector === view.sector && !world.planet.sun;
    const radiusOf = (world: World) => (world.planet.sun ? 12.5 : 8.6) * (near && !world.planet.sun ? 1.3 : 1);
    const bodies: { id: string; x: number; y: number; r: number }[] = [];
    const subjects: LabelSubject[] = [];
    worlds.forEach((world) => {
      if (!onView(world)) return;
      const r = radiusOf(world);
      bodies.push({ id: world.planet.id, x: world.at[0], y: world.at[1], r });
      if (isSubject(world)) {
        subjects.push({
          id: world.planet.id, x: world.at[0], y: world.at[1], r,
          rows: rowsOf(world.name, labelMode), rowsAlt: rowsAltOf(world.name),
          sun: world.planet.sun, outer: world.slot === last,
          slot: world.slot === null ? data.rings.length : world.slot,
        });
      }
    });
    const obstacles: Box[] = [];
    const texts: Box[] = [];
    data.rings.forEach((ring, slot) => {
      if (labelMode === "sector" || (labelMode === "ring" && ring.id !== view.ring)) return;
      const w = `${roman(slot)} · ${ring.name || ring.id} · 00/00`.length * 5 * textScale * GLYPH;
      const r = sky.radii[slot];
      texts.push([CENTRE - w / 2, CENTRE - r - 4.5, CENTRE + w / 2, CENTRE - r + 3]);
    });
    if (labelMode === "sky") {
      if (!hasSun) texts.push([CENTRE - 26, CENTRE + 5, CENTRE + 26, CENTRE + 17]);
      // the dividers: a name should not straddle a column edge
      sky.spans.forEach((span) => {
        [span.a0, span.a1].forEach((deg) => {
          for (let r = INNER - 9; r <= RIM; r += 7) {
            const [x, y] = polar(r, deg);
            obstacles.push([x - 1.6, y - 1.6, x + 1.6, y + 1.6]);
          }
        });
      });
      // the family names on the rim, as a run of small boxes along each arc
      rimLabels.forEach(({ angle, halfDeg }) => {
        const rl = RIM + 6.5;
        for (let k = 0; k <= 12; k += 1) {
          const [x, y] = polar(rl, angle - halfDeg + (2 * halfDeg * k) / 12);
          texts.push([x - 4.6, y - 4.6, x + 4.6, y + 4.6]);
        }
      });
    }
    return layoutLabels({ subjects, bodies, obstacles, texts, mode: labelMode, cam: cameraFor(view, sky, worlds, data.rings.length),
      textScale: labelScale(labelMode, textScale) });
  }, [worlds, data.rings, sky, textScale, rimLabels, hasSun, last], warmViews);

  // ── the guide ──────────────────────────────────────────────────────
  const guided = useMemo(() => guidedSet(guide, data), [guide, data]);
  const guideLine = useMemo(() => guideSentence(guide, guided, data), [guide, guided, data]);
  const frontierSlot = data.rings.findIndex((ring) => ring.id === data.frontier);

  // ── the hand ───────────────────────────────────────────────────────
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ x: number; y: number; px: number; py: number; f: number } | null>(null);
  const movedRef = useRef(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    setPan({ x: 0, y: 0 });
  }, [focus, ringSel, sectorSel]);

  const backOut = () => {
    if (zoomed) props.onClose();
    else if (sectorSel) props.onSector(null);
    else if (ringSel !== null) props.onRing(null);
  };

  const focused = focus ? worlds.get(focus) ?? null : null;
  const trackColourOf = track ? trackColour(P, track, hash32) : null;

  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${P.line}`,
        overflow: "hidden",
        background: P.panel,
        boxShadow: P.inset,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        viewBox={`0 0 ${FIELD} ${FIELD}`}
        role="group"
        aria-label="Your sky atlas"
        data-testid="atlas-orrery"
        style={{
          display: "block",
          touchAction: zoomed ? "none" : "auto",
          cursor: dragging ? "grabbing" : undefined,
        }}
        onPointerDown={(e) => {
          if (!zoomed || !svgRef.current) return;
          const r = svgRef.current.getBoundingClientRect();
          dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y, f: FIELD / r.width };
          movedRef.current = false;
          // Capture the pointer, or a mouse released outside the map never
          // delivers its pointerup here and the drag stays armed.
          try {
            (e.target as Element).setPointerCapture?.(e.pointerId);
          } catch {
            /* a browser that refuses capture still drags, just less well */
          }
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d) return;
          if (e.pointerType === "mouse" && e.buttons === 0) {
            dragRef.current = null;
            setDragging(false);
            return;
          }
          const dx = (e.clientX - d.x) * d.f;
          const dy = (e.clientY - d.y) * d.f;
          if (!movedRef.current && Math.hypot(dx, dy) > 5) {
            movedRef.current = true;
            setDragging(true);
          }
          if (movedRef.current) {
            const lim = 120 * cam.s;
            setPan({
              x: Math.max(-lim, Math.min(lim, d.px + dx)),
              y: Math.max(-lim, Math.min(lim, d.py + dy)),
            });
          }
        }}
        onPointerUp={(e) => {
          try {
            (e.target as Element).releasePointerCapture?.(e.pointerId);
          } catch {
            /* already released */
          }
          dragRef.current = null;
          setDragging(false);
        }}
        onPointerCancel={() => {
          dragRef.current = null;
          setDragging(false);
        }}
        onLostPointerCapture={() => {
          dragRef.current = null;
          setDragging(false);
        }}
        onClickCapture={(e) => {
          if (movedRef.current) {
            e.stopPropagation();
            movedRef.current = false;
          }
        }}
      >
        <g
          style={{
            transform: `translate(${cam.tx + pan.x}px,${cam.ty + pan.y}px) scale(${cam.s})`,
            transformOrigin: "0px 0px",
            transition: dragging ? "none" : `transform ${GLIDE}`,
          }}
        >
          <style>{KEYFRAMES}</style>
          <defs>
            <radialGradient id="atlas-bg" cx="50%" cy="46%" r="66%">
              <stop offset="0%" stopColor={P.bgIn} />
              <stop offset="100%" stopColor={P.bgOut} />
            </radialGradient>
            {[0, 1].map((i) => (
              <radialGradient
                key={i} id={`atlas-weave-${i}`} cx={CENTRE} cy={CENTRE} r={RIM + 6}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor={weaveColour(P, i)} stopOpacity="0" />
                <stop offset="0.36" stopColor={weaveColour(P, i)} stopOpacity={i ? 0.032 : 0.03} />
                <stop offset="0.85" stopColor={weaveColour(P, i)} stopOpacity={i ? 0.062 : 0.055} />
                <stop offset="1" stopColor={weaveColour(P, i)} stopOpacity="0" />
              </radialGradient>
            ))}
          </defs>

          <Backdrop P={P} zoomed={zoomed} interactive={mode !== "sky"} onClick={backOut} />

          {/* the sun's corona, when the sun is decoration rather than a world */}
          {!hasSun && (
            <g data-testid="atlas-sun" pointerEvents="none">
              {[...Array(8)].map((_, i) => {
                const a = (i * 45 * Math.PI) / 180;
                return (
                  <line
                    key={i}
                    x1={CENTRE + 5 * Math.cos(a)} y1={CENTRE + 5 * Math.sin(a)}
                    x2={CENTRE + 8.5 * Math.cos(a)} y2={CENTRE + 8.5 * Math.sin(a)}
                    stroke={P.gold} strokeOpacity="0.5" strokeWidth="0.7" {...NSS}
                  />
                );
              })}
              <circle cx={CENTRE} cy={CENTRE} r="3" fill={P.gold} fillOpacity="0.95" />
            </g>
          )}

          {/* the wedges — which family — and the names on the rim */}
          <g pointerEvents="none" style={{ opacity: zoomed ? 0 : 1, transition: "opacity 400ms" }}>
            {sky.sectors.map((sector, index) => {
              const span = sky.spans.get(sector);
              if (!span || index % 2 === 1) return null;
              // Every other column takes a wash; the run starts on the first
              // column after the notch so the untinted ground never joins up.
              return (
                <path
                  key={`weave-${sector}`} d={wedgePath(span, 6, RIM + 6)} fill="url(#atlas-weave-1)"
                  opacity={ringSel !== null ? 0.42 : 1} style={{ transition: "opacity 400ms" }}
                />
              );
            })}
            {sectorSel && sky.spans.get(sectorSel) && (
              <path d={wedgePath(sky.spans.get(sectorSel) as { a0: number; a1: number }, 6, RIM + 6)} fill={P.gold} opacity="0.05" />
            )}
            {sky.sectors.map((sector) => {
              const rim = rimLabels.get(sector);
              const span = sky.spans.get(sector);
              if (!rim || !span) return null;
              const rl = RIM + 6.5;
              const [lx, ly] = polar(rl, rim.angle);
              const bottom = Math.sin((rim.angle * Math.PI) / 180) > 0;
              const rot = bottom ? rim.angle - 90 : rim.angle + 90;
              const on = sectorSel === sector;
              const info = sectorInfo.get(sector);
              // What the reader actually sees of this name — the enclosing
              // group folds it away over an opened world — and so whether
              // it is a thing to tap or part of the ground behind it.
              const shown = (zoomed ? 0 : 1)
                * (ringSel !== null ? 0.35 : sectorSel && !on ? 0.26 : 0.85);
              return (
                <g
                  key={`rim-${sector}`} pointerEvents={taps(zoomedIn, shown)} style={{ cursor: "pointer" }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onSector(on ? null : sector);
                  }}
                  data-testid={`atlas-sector-label-${sector || "unfiled"}`}
                >
                  <path d={arcPath(CENTRE, CENTRE, rl, span.a0 + 2, span.a1 - 2)} fill="none" stroke="transparent" strokeWidth="12" />
                  <text
                    x={lx} y={ly + (bottom ? -1.2 : 1.8)} textAnchor="middle"
                    transform={`rotate(${rot.toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)})`}
                    style={{
                      fontFamily: MONO, fontSize: on ? RIM_FONT + 0.7 : RIM_FONT, letterSpacing: "0.1em",
                      fill: on ? P.gold : info ? sectorColour(P, sector, hash32) : P.dim,
                      opacity: shown,
                      transition: "fill 400ms, opacity 400ms",
                    }}
                  >
                    {rim.text}
                  </text>
                </g>
              );
            })}
          </g>

          {/* the spine — the dotted radial every ring's thread starts just clockwise of */}
          {data.rings.length > 0 && (
            <line
              x1={CENTRE} y1={CENTRE - RIM - 1} x2={CENTRE} y2={CENTRE - INNER + 18}
              opacity={ringSel !== null ? 0.25 : 1}
              stroke={P.faint} strokeOpacity="0.2" strokeWidth="0.6" strokeDasharray="1 3"
              {...NSS} pointerEvents="none"
            />
          )}

          {/* the rings: the track, the chain, the gate label */}
          {data.rings.map((ring, slot) => {
            const r = sky.radii[slot];
            const colour = ringColour(P, slot);
            const isFrontier = ring.id === data.frontier;
            const faded = ringSel !== null && !sectorSel && ringSel !== ring.id;
            const shown =
              (faded ? 0.07 : 1) *
              (!zoomed && guide === "frontier" && data.frontier
                ? isFrontier ? 1 : slot < frontierSlot ? 0.55 : 0.3
                : 1);
            // The gate's own type is quieter than its orbit, and folds away
            // entirely over an opened world.
            const gate = shown * (zoomed ? 0 : isFrontier ? 1 : 0.7);
            const gateTaps = taps(zoomedIn, gate);
            return (
              <g
                key={ring.id}
                style={{ opacity: shown, transition: "opacity 500ms" }}
              >
                {/* the track: a quiet solid thread — the chain links above it are
                    twice the weight and carry a glow, so progress still shouts */}
                <path
                  d={arcPath(CENTRE, CENTRE, r, GATE.a0, GATE.a0 + GATE.span)} fill="none" stroke={colour}
                  pointerEvents="none" strokeOpacity={isFrontier ? 0.3 : 0.17}
                  strokeWidth={isFrontier ? 0.55 : 0.45} {...NSS}
                />
                {(groups.get(ring.id) ?? []).map((members, gi) =>
                  members.map((id, i) => {
                    const here = worlds.get(id);
                    const before = i > 0 ? worlds.get(members[i - 1]) : undefined;
                    const opened = !!here && here.planet.state !== "COLD";
                    const previousOpened = !!before && before.planet.state !== "COLD";
                    const show = links === "chain" ? i > 0 && opened && previousOpened : opened;
                    if (!show || !here) return null;
                    const to = sky.angles.get(id) as number;
                    const step = members.length > 1
                      ? Math.abs((sky.angles.get(members[1]) as number) - (sky.angles.get(members[0]) as number))
                      : 0;
                    const from = i > 0 ? (sky.angles.get(members[i - 1]) as number) : to - Math.max(6, step * 0.5);
                    return (
                      <path
                        key={`chain-${gi}-${id}`} pointerEvents="none"
                        d={arcPath(CENTRE, CENTRE, r, from, to)}
                        fill="none" stroke={colour} strokeOpacity="0.9" strokeWidth="1.5" {...NSS}
                        style={{ filter: `drop-shadow(0 0 3px ${colour}66)` }}
                      />
                    );
                  }),
                )}
                <text
                  x={CENTRE} y={CENTRE - r + 1.8} textAnchor="middle"
                  onClick={() => props.onRing(ringSel === ring.id ? null : ring.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      props.onRing(ringSel === ring.id ? null : ring.id);
                    }
                  }}
                  role="button" tabIndex={gateTaps === "auto" ? 0 : -1}
                  aria-label={`${ring.name || ring.id}, ${ring.crossed} of ${ring.total} crossed`}
                  data-testid={`atlas-ring-label-${ring.id}`}
                  style={{
                    fontFamily: MONO, fontSize: 5 * textScale, letterSpacing: "0.12em", fill: colour,
                    cursor: "pointer", opacity: zoomed ? 0 : isFrontier ? 1 : 0.7,
                    pointerEvents: gateTaps,
                    transition: "opacity 300ms",
                  }}
                >
                  {`${roman(slot)} · ${(ring.name || ring.id).toUpperCase()} · ${ring.crossed}/${ring.total}`}
                </text>
              </g>
            );
          })}

          {/* the tails: each opened world drags an arc behind it along its
              own orbit, as far as its climb toward the next rung has got */}
          {data.rings.map((ring, slot) =>
            ring.planets.map((id) => {
              const world = worlds.get(id);
              if (!world || world.planet.sun || world.planet.state === "COLD") return null;
              const climbing = world.planet.rungs.filter((rung) => rung.binding && !rung.met);
              const reach = climbing.length ? climbing[0].progress : 1;
              if (reach <= 0.02) return null;
              const r = sky.radii[slot];
              const head = (sky.angles.get(id) as number) - 4.5;
              const tail = head - (2 + 26 * reach);
              const colour = seasonColour(P, world.planet.heat);
              return (
                <g
                  key={`tail-${id}`} pointerEvents="none"
                  opacity={ringSel !== null && !sectorSel && ringSel !== ring.id ? 0.06 : 1}
                  style={{ transition: "opacity 500ms" }}
                >
                  <path d={arcPath(CENTRE, CENTRE, r, tail, head)} fill="none" stroke={colour} strokeOpacity="0.32" strokeWidth="3.4" strokeLinecap="round" {...NSS} />
                  <path d={arcPath(CENTRE, CENTRE, r, tail + (head - tail) * 0.45, head)} fill="none" stroke={colour} strokeOpacity="0.85" strokeWidth="1.6" strokeLinecap="round" {...NSS} />
                </g>
              );
            }),
          )}

          {/* the drift — where unplaced worlds wait for an author */}
          {data.unplaced.length > 0 && (
            <g pointerEvents="none">
              <circle cx={CENTRE} cy={CENTRE} r={DRIFT} fill="none" stroke={P.faint} strokeOpacity="0.22" strokeWidth="0.5" strokeDasharray="1 4" {...NSS} />
              {!zoomed && (
                <text
                  x={CENTRE} y={CENTRE - DRIFT + 1.8} textAnchor="middle"
                  style={{ fontFamily: MONO, fontSize: 5 * textScale, letterSpacing: "0.12em", fill: P.faint, opacity: 0.7 }}
                >
                  UNPLACED
                </text>
              )}
            </g>
          )}

          {/* prerequisite edges, only while a world is open */}
          {focused && (
            <g pointerEvents="none">
              {focused.planet.prereqs.map((id) => {
                const from = sky.positions.get(id);
                if (!from) return null;
                const crossed = worlds.get(id)?.planet.state === "CROSSED";
                return (
                  <line
                    key={`u${id}`} x1={from[0]} y1={from[1]} x2={focused.at[0]} y2={focused.at[1]}
                    stroke={P.gold} strokeOpacity={crossed ? 0.5 : 0.26} strokeWidth="0.7" strokeDasharray="2 2.6" {...NSS}
                  />
                );
              })}
              {focused.planet.opens.map((id) => {
                const to = sky.positions.get(id);
                if (!to) return null;
                return (
                  <line
                    key={`d${id}`} x1={focused.at[0]} y1={focused.at[1]} x2={to[0]} y2={to[1]}
                    stroke={P.faint} strokeOpacity="0.22" strokeWidth="0.6" strokeDasharray="1 2.6" {...NSS}
                  />
                );
              })}
            </g>
          )}

          {/* the worlds */}
          {data.planets.map((planet) => {
            const world = worlds.get(planet.id);
            if (!world) return null;
            const isFocus = focus === planet.id;
            const inRing = ringSel !== null && planet.ring === ringSel;
            const inSector = !!sectorSel && world.sector === sectorSel;
            const emph = zoomed || guided === null ? 1 : guided.has(planet.id) ? 1 : 0.14;
            const onTrack = track === null || planet.tracks.includes(track);
            const ringFade = ringSel !== null && !sectorSel ? (inRing ? 1 : 0.06) : 1;
            const sectorFade = sectorSel && !zoomed ? (inSector || planet.sun ? 1 : 0.07) : 1;
            const dimmed = zoomed
              ? isFocus ? 1 : 0.12
              : ringFade * sectorFade * emph * (onTrack ? 1 : 0.12);
            const swell = isFocus
              ? 1
              : (!zoomed && !planet.sun && (sectorSel ? inSector : inRing) ? 1.3 : 1) * (planet.sun ? 1.22 : 1);
            const node = (
              <PlanetNode
                key={planet.id}
                planet={planet} at={world.at} slot={world.slot} P={P} gauge={gauge}
                placement={zoomed ? undefined : placements.get(planet.id)}
                dimmed={dimmed} swell={swell} isFocus={isFocus} zoomed={zoomed}
                unlit={unlit} credit={credit}
                guideMark={!zoomed && guided?.has(planet.id) ? (guide === "cooling" ? "cooling" : "next") : null}
                desaturate={!zoomed && guide === "cooling" && emph < 1}
                trackHalo={!zoomed && track && onTrack ? trackColourOf : null}
                tabbable={!isFocus && dimmed >= LIVE}
                hit={taps(zoomedIn, dimmed)}
                onPlanet={props.onPlanet}
              >
                {isFocus && (
                  <OpenPlanet
                    planet={planet} slot={world.slot} P={P} nss={NSS} gauge={gauge} credit={credit}
                    selClaim={selClaim} onClaim={props.onClaim} onClose={props.onClose}
                  />
                )}
              </PlanetNode>
            );
            return planet.sun ? <g key={planet.id} data-testid="atlas-sun">{node}</g> : node;
          })}
        </g>
      </svg>

      <div
        style={{
          textAlign: "center",
          padding: "0 16px 10px",
          fontFamily: MONO,
          fontSize: 8,
          color: P.faint,
          lineHeight: 1.7,
        }}
      >
        {mode === "world"
          ? "tap a claim to open its shelf · drag to wander · tap the space around to sail out"
          : mode === "ring"
            ? "tap a world to walk its claims · tap the space around to pull back"
            : mode === "sector"
              ? "one family, ring by ring · tap a world to walk its claims · tap the space around to pull back"
              : "wedges = which family · rings = how deep · tap any world, a ring's name, or a family on the rim"}
        {!zoomed && guideLine && (
          <div style={{ color: P.gold, marginTop: 4 }} data-testid="atlas-guide-line">
            {guideLine}
          </div>
        )}
      </div>
    </div>
  );
}

// ── the backdrop ─────────────────────────────────────────────────────

const Backdrop = memo(function Backdrop({
  P, zoomed, interactive, onClick,
}: {
  P: Palette;
  zoomed: boolean;
  interactive: boolean;
  onClick: () => void;
}) {
  return (
    <>
      {/* Reaches well past the field: the widest camera (a column framed
          at its floor) shows a little beyond it, and a tap meant for the
          way out must never land on nothing. */}
      <rect
        x="-160" y="-160" width={FIELD + 320} height={FIELD + 320} fill="url(#atlas-bg)"
        onClick={onClick}
        style={{ cursor: zoomed || interactive ? "zoom-out" : undefined }}
      />
      {/* the starfield — a fixed modular sequence, so the same sky every
          render and no random in the drawing */}
      {P.night &&
        [...Array(30)].map((_, i) => {
          const x = (i * 97.31 + 11) % FIELD;
          const y = (i * 57.17 + 29) % FIELD;
          return (
            <circle
              key={`bg${i}`} cx={x} cy={y} r={0.4 + ((i * 31) % 8) / 13}
              fill="#DDE6FF" opacity={0.08 + ((i * 13) % 9) / 60} pointerEvents="none"
            />
          );
        })}
      {[[-30, 30, 0, 13], [200, -28, 6.4, 17], [40, -34, 10.8, 21]].map(([sx, sy, delay, dur], i) => (
        <g
          key={`sh${i}`} pointerEvents="none" data-testid="atlas-shooting-star"
          style={{ animation: `zshoot ${dur}s linear ${delay}s infinite` }}
        >
          <line
            x1={sx} y1={sy} x2={sx - 24} y2={sy - 15.4}
            stroke={P.night ? "#EAF2FF" : P.goldHi} strokeOpacity={P.night ? 0.65 : 0.35}
            strokeWidth="0.8" strokeLinecap="round"
          />
          <circle cx={sx} cy={sy} r="1.05" fill={P.night ? "#FFFFFF" : P.goldHi} fillOpacity={P.night ? 0.95 : 0.6} />
        </g>
      ))}
    </>
  );
});

// ── one world ────────────────────────────────────────────────────────

type NodeProps = {
  planet: PlanetReading;
  at: Point;
  slot: number | null;
  P: Palette;
  gauge: string[];
  placement: Placement | undefined;
  dimmed: number;
  swell: number;
  isFocus: boolean;
  zoomed: boolean;
  unlit: Unlit;
  credit: CreditStyle;
  guideMark: "next" | "cooling" | null;
  desaturate: boolean;
  trackHalo: string | null;
  tabbable: boolean;
  /** Whether a tap here opens this world, or falls through to the
      backdrop and sails back out — see `taps`. */
  hit: "auto" | "none";
  onPlanet: (id: string) => void;
  children?: React.ReactNode;
};

/**
 * A world, memoised: forty of these redraw on every state change
 * otherwise, and most changes touch one.  Every prop is a primitive or
 * an object the parent memoises, so a world that did not change is not
 * re-rendered.
 */
const PlanetNode = memo(function PlanetNode({
  planet, at, slot, P, gauge, placement, dimmed, swell, isFocus, zoomed, unlit, credit,
  guideMark, desaturate, trackHalo, tabbable, hit, onPlanet, children,
}: NodeProps) {
  const [x, y] = at;
  const colour = planet.state === "COLD" ? P.faint : seasonColour(P, planet.heat);
  const drift = driftOf(planet.id);
  const isSun = planet.sun;
  const label = placement ? labelGeometry(placement, swell, isSun) : null;
  return (
    <g
      style={
        isSun
          ? undefined
          : {
              transformOrigin: `${CENTRE}px ${CENTRE}px`,
              animation: `zdrift${drift.direction} ${drift.duration}s ease-in-out ${drift.delay}s infinite alternate`,
            }
      }
    >
      <g
        style={{
          transform: `translate(${x}px,${y}px) scale(${swell})`,
          opacity: dimmed,
          filter: desaturate ? "saturate(0.15)" : undefined,
          transition: "opacity 500ms, transform 500ms",
        }}
      >
        <g
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onPlanet(planet.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPlanet(planet.id);
            }
          }}
          role="button"
          tabIndex={tabbable ? 0 : -1}
          aria-label={`${planet.title || planet.id} — ${planet.sentence}`}
          data-testid={`atlas-planet-${planet.id}`}
          data-planet-state={planet.state}
          style={{
            cursor: "pointer",
            opacity: isFocus ? 0 : 1,
            transition: "opacity 300ms",
            // The opened world hands its surface to `OpenPlanet`; a world
            // the view has faded hands its surface to the backdrop.
            pointerEvents: isFocus ? "none" : hit,
          }}
        >
          <circle r={isSun ? 14 : 12} fill="transparent" />
          {/* the sun's corona — the world itself is drawn like the others */}
          {isSun && !zoomed &&
            [...Array(8)].map((_, i) => {
              const a = ((i * 45 + 22.5) * Math.PI) / 180;
              return (
                <line
                  key={i}
                  x1={9 * Math.cos(a)} y1={9 * Math.sin(a)} x2={12.5 * Math.cos(a)} y2={12.5 * Math.sin(a)}
                  stroke={P.gold} strokeOpacity="0.45" strokeWidth="0.7" {...NSS} pointerEvents="none"
                />
              );
            })}
          <g
            style={{
              // One breath for every opened world, staggered per world.
              // A molten one has its own slow ember pulse on the seams
              // (see `PlanetBody`), which is enough life for the sky
              // without a second rhythm competing with this one.
              transformOrigin: "0px 0px",
              animation:
                planet.state === "COLD"
                  ? "none"
                  : `zbreathe 4.8s ease-in-out ${drift.twinkleDelay}s infinite`,
            }}
          >
            {/* the route halo — a world this audience is routed through wears its colour */}
            {trackHalo && (
              <circle
                r={isSun ? 15.5 : 12.4} fill="none" stroke={trackHalo} strokeWidth="0.95" strokeOpacity="0.9"
                {...NSS} pointerEvents="none" data-testid="atlas-track-halo"
              />
            )}
            {guideMark === "cooling" && (
              <circle r="10.8" fill={seasonColour(P, planet.heat)} opacity="0.14" pointerEvents="none" />
            )}
            {guideMark === "next" && (
              <circle r="13.4" fill="none" stroke={P.gold} strokeWidth="0.5" strokeOpacity="0.8" strokeDasharray="1.5 2.6" {...NSS} pointerEvents="none" />
            )}
            {planet.state === "CROSSED" && (
              <>
                <circle r="8.2" fill={colour} opacity={P.night ? 0.2 : 0.14} />
                <circle r="6.8" fill="none" stroke={colour} strokeWidth="0.8" strokeOpacity="0.9" {...NSS} />
                {/* radiant: the top of the ladder, crossed on evidence — the
                    one look the sky reserves for a world that is finished */}
                <circle r="11.4" fill="none" stroke={colour} strokeWidth="0.5" strokeOpacity="0.5" strokeDasharray="0.5 2.2" {...NSS} />
              </>
            )}
            {/* placed, not earned: the same halo, drawn open, so borrowed
                ground never wears the crossed ring */}
            {planet.state === "GRANTED" && (
              <circle r="6.8" fill="none" stroke={P.gold} strokeWidth="0.8" strokeOpacity="0.75" strokeDasharray="1.6 1.6" {...NSS} />
            )}
            <PlanetBody planet={planet} slot={slot} s={5.2} P={P} nss={NSS} unlit={unlit} />
            {!isFocus && (
              <Mastery
                rungs={planet.rungs.filter((rung) => rung.binding && (rung.active || rung.met))}
                R={7.2} colours={gauge} P={P} nss={NSS} dim={planet.state === "COLD"}
                credit={credit} idPrefix={`sky-${planet.id}-`}
              />
            )}
            {planet.standing === "granted" && !isFocus && (
              <g pointerEvents="none">
                <circle cx={12.4 * Math.cos((Math.PI * 5) / 6)} cy={12.4 * Math.sin((Math.PI * 5) / 6)} r="1.9" fill="none" stroke={P.gold} strokeWidth="0.6" />
                <circle cx={12.4 * Math.cos((Math.PI * 5) / 6)} cy={12.4 * Math.sin((Math.PI * 5) / 6)} r="0.55" fill={P.gold} />
              </g>
            )}
          </g>
          {/* the name, where the label engine seated it; a leader when it
              stands off its world.  Not drawn inside a world: names read
              through the veil that hides the bodies. */}
          {label && (
            <g pointerEvents="none">
              {label.leader && (
                <line
                  x1={label.leader.x1} y1={label.leader.y1} x2={label.leader.x2} y2={label.leader.y2}
                  stroke={P.faint} strokeOpacity="0.42" strokeWidth="0.38" {...NSS}
                />
              )}
              <text
                x={label.x} y={label.y} textAnchor={label.anchor}
                style={{
                  fontFamily: MONO, fontSize: label.fontSize,
                  fill: planet.state === "COLD" ? P.faint : P.dim, opacity: 0.95,
                }}
              >
                {label.rows.map((row, i) => (
                  <tspan key={i} x={label.x} dy={i === 0 ? -(label.rows.length - 1) * label.fontSize * 1.15 : label.fontSize * 1.15}>
                    {row}
                  </tspan>
                ))}
              </text>
            </g>
          )}
        </g>
        {children}
      </g>
    </g>
  );
});

/**
 * The engine seats a name in field units around the world's centre; the
 * world is drawn inside a group scaled by `swell`, so the text is set at
 * the inverse and lands where the engine measured it.
 */
function labelGeometry(placement: Placement, swell: number, sun: boolean) {
  const x = placement.lx / swell;
  const y = placement.ly / swell;
  const fontSize = placement.fontSize / swell;
  const tx = x + (placement.anchor === "start" ? -1.6 : placement.anchor === "end" ? 1.6 : 0);
  const ty = y - fontSize * 0.34;
  const dd = Math.hypot(tx, ty) || 1;
  let leader: { x1: number; y1: number; x2: number; y2: number } | null = null;
  if (dd >= (placement.lead ? 5.5 : 10.5)) {
    const ux = tx / dd;
    const uy = ty / dd;
    const from = sun ? 11 : 7.6;
    const to = dd - 2.4;
    if (to > from + 0.8) leader = { x1: ux * from, y1: uy * from, x2: ux * to, y2: uy * to };
  }
  return { x, y, fontSize, anchor: placement.anchor, rows: placement.rows, leader };
}

// ── the guide ────────────────────────────────────────────────────────

/** Which worlds the active guide wants your eye on; null for no guide. */
function guidedSet(guide: Guide, data: AtlasPayload): Set<string> | null {
  if (guide === "none") return null;
  if (guide === "frontier") {
    return new Set(
      data.planets.filter((planet) => planet.ring === data.frontier && planet.state !== "CROSSED").map((p) => p.id),
    );
  }
  if (guide === "cooling") {
    return new Set(
      data.planets.filter((planet) => planet.claimsBurned > 0 || planet.claimsCooling > 0).map((p) => p.id),
    );
  }
  // "next": the worlds whose ground is already under them — every
  // prerequisite crossed or standing — and whose own top rung is not met.
  // Repair before advance: a world with a live misconception comes first.
  const done = new Set(
    data.planets.filter((planet) => planet.state === "CROSSED" || planet.state === "GRANTED").map((p) => p.id),
  );
  const ready = data.planets.filter(
    (planet) => planet.state !== "CROSSED" && planet.prereqs.every((id) => done.has(id)),
  );
  const wounded = ready.filter((planet) => planet.claimsBurned > 0);
  const cooling = ready.filter((planet) => !planet.claimsBurned && planet.claimsCooling > 0);
  const fresh = ready.filter((planet) => !planet.claimsBurned && !planet.claimsCooling);
  return new Set([...wounded, ...cooling, ...fresh].slice(0, 4).map((planet) => planet.id));
}

/** The active guide, said in one line — what the highlighting means. */
function guideSentence(guide: Guide, guided: Set<string> | null, data: AtlasPayload): string {
  if (guide === "none" || guided === null) return "";
  const named = data.planets.filter((planet) => guided.has(planet.id)).map((planet) => planet.title || planet.id);
  if (guide === "frontier") {
    const ring = data.rings.find((entry) => entry.id === data.frontier);
    return ring
      ? `lit = your frontier at ${ring.name || ring.id} · faint = still ahead`
      : "every ring is crossed — the sky is yours";
  }
  if (guide === "cooling") {
    return named.length ? `warm these first — ${named.slice(0, 3).join(" · ")}` : "nothing is slipping — the sky is fresh";
  }
  if (!named.length) return "every world within reach is crossed";
  const wounded = data.planets.filter((planet) => guided.has(planet.id) && planet.claimsBurned);
  if (wounded.length) {
    const first = wounded[0].title || wounded[0].id;
    return `repair first — ${first} · then ${named.filter((name) => name !== first).slice(0, 2).join(" · ")}`;
  }
  return `open next — ${named.slice(0, 3).join(" · ")}`;
}

export type { SectorReading };
